import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ChevronRight, Sparkles, ExternalLink, ArrowLeft, Star, Camera, HeartPulse, Wrench, Flame, CheckCircle2 } from "lucide-react";
import { getContent, http } from "@/lib/api";
import { TYPES, SEGMENT_TO_TYPE, linkTo, getTitle, getImage } from "@/config";
import { ShareBar } from "@/components/ShareBar";
import { Seo } from "@/components/Seo";

const URL_RE = /(https?:\/\/[^\s]+)/g;
const IS_URL = /^https?:\/\/[^\s]+$/;

function youTubeId(text) {
  const m = String(text).match(/https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

function linkify(text) {
  const parts = String(text).split(URL_RE);
  return parts.map((part, i) => {
    if (IS_URL.test(part)) {
      const clean = part.replace(/[.,);]+$/, "");
      return (
        <a key={i} href={clean} target="_blank" rel="noreferrer"
          className="text-gold underline decoration-gold/40 underline-offset-2 hover:decoration-gold break-words">
          {clean}
        </a>
      );
    }
    return part;
  });
}

const META_FIELDS = {
  cases: [
    ["status", "Status"], ["category", "Category"], ["date_start", "Date"], ["date_end", "Resolved"],
    ["era", "Era"], ["region", "Region"], ["location", "Location"], ["estimated_value", "Estimated Value"],
  ],
  artworks: [
    ["artist", "Artist"], ["year_created", "Created"], ["medium", "Medium"], ["dimensions", "Dimensions"],
    ["current_status", "Status"], ["estimated_value", "Value"],
  ],
  people: [
    ["nationality", "Nationality"], ["born", "Born"], ["died", "Died"],
  ],
  museums: [
    ["location", "Location"], ["country", "Country"], ["founded", "Founded"],
  ],
  episodes: [
    ["episode_number", "Episode"], ["season", "Season"], ["duration", "Duration"], ["published_at", "Published"],
  ],
};

const REL_LABELS = {
  people_ids: "People", artwork_ids: "Artworks", museum_ids: "Museums",
  related_case_ids: "Related Cases", case_ids: "Related Cases", guest_ids: "Guests",
  related_news_ids: "In the News",
};

function fmtDate(v) {
  if (!v) return v;
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });}

export default function Detail() {
  const { segment, slug } = useParams();
  const ctype = SEGMENT_TO_TYPE[segment];
  const [data, setData] = useState(undefined);
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    if (!ctype) return;
    setData(undefined);
    setSummary("");
    getContent(ctype, slug).then(setData).catch(() => setData(null));
    window.scrollTo(0, 0);
    // Re-fetch when the route (ctype/slug) changes; getContent/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctype, slug]);

  if (!ctype) return <Navigate to="/" replace />;
  if (data === undefined) return <div className="mx-auto max-w-4xl px-5 py-32 eyebrow">Loading…</div>;
  if (data === null) return <div className="mx-auto max-w-4xl px-5 py-32 font-serif text-3xl text-faint">Entry not found.</div>;

  const item = data.item;
  const related = data.related || {};
  const t = TYPES[ctype];
  const img = getImage(item);
  const body = item.body || item.show_notes || item.bio || item.description || "";
  const metaFields = (META_FIELDS[ctype] || []).filter(([k]) => item[k]);

  const summarize = async () => {
    setSummarizing(true);
    try {
      const { data: r } = await http.post("/ai/summarize", { title: getTitle(item), text: body });
      setSummary(r.summary);
    } finally {
      setSummarizing(false);
    }
  };

  const isArticle = t.kind === "article";
  const canSummarize = body.length > 300;
  const shareTitle = getTitle(item);
  const shareDesc =
    item.summary || item.dek || item.subtitle ||
    (body ? body.replace(/\s+/g, " ").trim().slice(0, 180) : "");

  return (
    <article>
      <Seo title={shareTitle} description={shareDesc} image={img} type={isArticle ? "article" : "website"} />
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1100px] px-5 lg:px-10 pt-10">
        <nav className="flex items-center gap-2 text-xs text-faint" data-testid="breadcrumb">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={12} />
          <Link to={`/${t.segment}`} className="hover:text-gold">{t.label}</Link>
          <ChevronRight size={12} />
          <span className="text-parchment/60 truncate">{getTitle(item)}</span>
        </nav>
      </div>

      {/* Header */}
      <header className="mx-auto max-w-[1100px] px-5 lg:px-10 pt-8 pb-10">
        <div className="eyebrow mb-4 text-gold">
          {t.singular}
          {item.category ? ` · ${item.category}` : ""}
          {(item.roles || []).length ? ` · ${item.roles.join(" · ")}` : ""}
        </div>
        <h1 className="font-serif text-4xl leading-[1.02] tracking-tight text-parchment sm:text-5xl lg:text-6xl">
          {getTitle(item)}
        </h1>
        {item.subtitle && <p className="mt-4 font-serif text-2xl italic text-faint">{item.subtitle}</p>}
        {item.dek && <p className="mt-5 max-w-3xl font-serif text-2xl leading-snug text-faint">{item.dek}</p>}
        {(item.author || item.published_at || item.reading_time) && (
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-faint">
            {item.author && <span className="text-parchment/80">By {item.author}</span>}
            {item.published_at && <span>{fmtDate(item.published_at)}</span>}
            {item.reading_time && <span>· {item.reading_time}</span>}
          </div>
        )}
        <div className="mt-7 border-t border-line pt-5">
          <ShareBar title={shareTitle} summary={shareDesc} />
        </div>
      </header>

      {/* Hero image */}
      {img && (
        <div className="mx-auto max-w-[1100px] px-5 lg:px-10">
          <div className="overflow-hidden border border-line">
            <img src={img} alt={getTitle(item)} className="w-full object-cover grayscale-[12%]"
              style={{ maxHeight: 560, objectPosition: item.image_position || (ctype === "people" ? "center top" : "center") }} />
          </div>
          {item.image_credit && (
            <p className="mt-2 text-right font-mono text-xs text-faint" data-testid="image-credit">
              {item.image_credit}
            </p>
          )}
        </div>
      )}

      <div className="mx-auto max-w-[1100px] px-5 lg:px-10 py-14 grid gap-14 lg:grid-cols-[1fr_300px]">
        {/* Main column */}
        <div>
          {ctype === "mishaps" && <MishapReport item={item} />}

          {/* Summary/lead */}
          {item.summary && !isArticle && (
            <p className="mb-10 font-serif text-2xl leading-relaxed text-parchment">{item.summary}</p>
          )}

          {/* AI summary */}
          {canSummarize && (
            <div className="mb-10 border border-line bg-surface p-5" data-testid="ai-summary-block">
              {!summary ? (
                <button
                  data-testid="summarize-button"
                  onClick={summarize}
                  disabled={summarizing}
                  className="inline-flex items-center gap-2 eyebrow text-gold hover:text-gold-hover disabled:opacity-60"
                >
                  <Sparkles size={14} /> {summarizing ? "Summarizing…" : "Generate an AI abstract"}
                </button>
              ) : (
                <>
                  <div className="eyebrow mb-2 flex items-center gap-2 text-gold"><Sparkles size={13} /> Abstract</div>
                  <p className="font-serif text-lg leading-relaxed text-parchment">{summary}</p>
                </>
              )}
            </div>
          )}

          {/* Body */}
          {body && (
            <div className={isArticle || ctype === "cases" ? "prose-editorial" : "space-y-5 text-lg leading-relaxed text-parchment/80"}>
              {body.split("\n\n").map((p, i) => {
                const ytId = youTubeId(p);
                if (ytId) {
                  const caption = p.replace(URL_RE, "").trim();
                  return (
                    <div key={i} className="my-8">
                      {caption && <p className="embed-caption mb-3">{caption}</p>}
                      <div className="aspect-video w-full overflow-hidden border border-line" data-testid="body-youtube-embed">
                        <iframe
                          className="h-full w-full"
                          src={`https://www.youtube.com/embed/${ytId}`}
                          title="Embedded video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  );
                }
                return <p key={i}>{linkify(p)}</p>;
              })}
            </div>
          )}

          {/* Tags */}
          {(item.tags || []).length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`}
                  className="border border-line px-3 py-1 font-mono text-xs text-faint hover:border-gold/50 hover:text-gold">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {item.website && (
            <a href={item.website} target="_blank" rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 eyebrow text-gold hover:text-gold-hover">
              Official website <ExternalLink size={13} />
            </a>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:border-l lg:border-line lg:pl-8">
          {metaFields.length > 0 && (
            <div className="mb-10">
              <div className="eyebrow mb-4 border-b border-gold/30 pb-2">Dossier</div>
              <dl className="space-y-3">
                {metaFields.map(([k, label]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-line pb-3">
                    <dt className="eyebrow text-faint">{label}</dt>
                    <dd className="text-right text-sm text-parchment">
                      {k.includes("published") ? fmtDate(item[k]) : String(item[k])}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {Object.entries(related).map(([field, entries]) => (
            <div key={field} className="mb-10" data-testid={`related-${field}`}>
              <div className="eyebrow mb-4 border-b border-gold/30 pb-2">{REL_LABELS[field] || "Linked"}</div>
              <ul className="space-y-4">
                {entries.map((e) => (
                  <li key={e.id}>
                    <Link to={linkTo(e._collection, e.slug)} data-testid={`related-link-${e.slug}`}
                      className="group flex items-center gap-3">
                      {(e.hero_image || e.image) && (
                        <span className="h-12 w-12 shrink-0 overflow-hidden border border-line">
                          <img src={e.hero_image || e.image} alt="" className="h-full w-full object-cover grayscale" />
                        </span>
                      )}
                      <span className="font-serif text-lg leading-tight text-parchment group-hover:text-gold transition-colors">
                        {e.title || e.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <Link to={`/${t.segment}`} className="inline-flex items-center gap-2 eyebrow text-faint hover:text-gold">
            <ArrowLeft size={13} /> All {t.label}
          </Link>
        </aside>
      </div>
    </article>
  );
}


function MishapReport({ item }) {
  const rating = Math.max(0, Math.min(5, parseInt(item.damage_rating, 10) || 0));
  const rows = [
    { icon: Wrench, label: "Repair Cost", value: item.repair_cost || "Undisclosed" },
    { icon: HeartPulse, label: "Curator's Blood Pressure", value: item.blood_pressure || "Elevated" },
    { icon: Flame, label: "Embarrassment Level", value: item.embarrassment || "High" },
    { icon: CheckCircle2, label: "Recovered?", value: item.recovered || "Unknown" },
    { icon: Camera, label: "Selfie Involved?", value: item.selfie || "No comment" },
  ];
  return (
    <div className="mb-10 border border-gold/40 bg-surface" data-testid="mishap-report">
      <div className="flex items-center justify-between border-b border-gold/30 px-5 py-3">
        <span className="eyebrow text-gold">Incident Report</span>
        <span className="font-mono text-xs text-faint">COA / MISHAPS</span>
      </div>
      <div className="px-5 py-5">
        <div className="mb-5 flex items-center justify-between border-b border-line pb-4">
          <span className="eyebrow">Accidental Damage Rating</span>
          <span className="flex gap-1" aria-label={`${rating} out of 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={18} className={n <= rating ? "fill-gold text-gold" : "text-line"} />
            ))}
          </span>
        </div>
        <dl className="space-y-3">
          {rows.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center justify-between gap-4">
              <dt className="flex items-center gap-2 eyebrow text-faint">
                <Icon size={14} className="text-gold/70" /> {label}
              </dt>
              <dd className="text-right font-mono text-sm text-parchment">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
